import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import * as yup from 'yup'

const defaultValues = {
  // Upload Form
  formatName: '',
  formatType: null,
  fileContainsIssuedDate: true,
  fileContainsHeaderRow: true,
  rowsToSkipAtStart: 0,
  rowsToSkipAtEnd: 0,
  skipRowsEnabled: false,
  skipRowsFrom: null,
  skipRowsTo: null,
  textQualifier: null,
  sampleFile: '',

  dateInFileDontIncludeSeparators: false,
  issuedDateFormat: null,
  //State
  checkNumber: null,
  amount: null,
  issuedDate: null,
  accountNumber: null,
  issuedPayee: null,
  recordType: null,

  // File Config
  checkNumberFrom: null,
  checkNumberTo: null,

  amountFrom: null,
  amountTo: null,

  issuedDateFrom: null,
  issuedDateTo: null,

  accountNumberFrom: null,
  accountNumberTo: null,

  issuedPayeeFrom: null,
  issuedPayeeTo: null,
}

const mappingFieldsDefault = {
  checkNumber: null,
  amount: null,
  issuedDate: null,
  dateInFileDontIncludeSeparators: false,
  issuedDateFormat: null,
  accountNumber: null,
  issuedPayee: null,
  recordType: null,
  // add new fields
  checkNumberFrom: null,
  checkNumberTo: null,

  amountFrom: null,
  amountTo: null,

  issuedDateFrom: null,
  issuedDateTo: null,

  accountNumberFrom: null,
  accountNumberTo: null,

  issuedPayeeFrom: null,
  issuedPayeeTo: null,
}

export interface IFileMapping {
  formatName: string
  formatType: string
  fileContainsIssuedDate: boolean
  fileContainsHeaderRow: boolean
  rowsToSkipAtStart: number
  rowsToSkipAtEnd: number
  skipRowsEnabled: boolean
  skipRowsFrom: number | null
  skipRowsTo: number | null
  textQualifier: string
  sampleFile: string

  checkNumber: string
  amount: string
  issuedDate: string
  accountNumber: string
  issuedPayee: string

  checkNumberFrom: number
  checkNumberTo: number
  amountFrom: number
  amountTo: number
  issuedDateFrom: number
  issuedDateTo: number
  dateInFileDontIncludeSeparators: false
  issuedDateFormat: string
  accountNumberFrom: number
  accountNumberTo: number
  issuedPayeeFrom: number
  issuedPayeeTo: number
}

/** US32690 - upper bound of the optional Row Skip Range, same domain already used by Rows To Skip At Start/End. */
export const SKIP_ROWS_MAX = 99

/** Treats an emptied number input as "not filled" instead of NaN, so the range can be cleared. */
const emptyStringToNull = (value: any, originalValue: any) =>
  originalValue === '' || originalValue === null || originalValue === undefined ? null : value

function generateValidationSchema(t: any, min: number = 1, max: number = 255) {
  const validationSchema = yup
    .object()
    .shape({
      formatName: yup.string().required(
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
          Required_Field: t('FileMappingSetup:addFileMapping.label.formatName'),
        }),
      ),
      formatType: yup.string().required(
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
          Required_Field: `${t('FileMappingSetup:addFileMapping.label.formatType')}`,
        }),
      ),
      sampleFile: yup.string().when('formatType', (val) => {
        if (val[0]) {
          return yup.string().required(
            t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
              Required_Field: t('FileMappingSetup:addFileMapping.label.sampleFile'),
            }),
          )
        }
        return yup.string().notRequired()
      }),
      skipRowsEnabled: yup.boolean(),
      skipRowsFrom: yup
        .number()
        .nullable()
        .transform(emptyStringToNull)
        .when('skipRowsEnabled', (val) => {
          if (!val[0]) return yup.number().nullable().transform(emptyStringToNull).notRequired()

          return yup
            .number()
            .nullable()
            .transform(emptyStringToNull)
            .required(
              t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                Required_Field: t('FileMappingSetup:addFileMapping.label.skipRows'),
              }),
            )
            .min(
              1,
              t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
              }),
            )
            .max(
              SKIP_ROWS_MAX,
              t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                Max_Column: SKIP_ROWS_MAX,
                From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
              }),
            )
        }),
      skipRowsTo: yup
        .number()
        .nullable()
        .transform(emptyStringToNull)
        .when('skipRowsEnabled', (val) => {
          if (!val[0]) return yup.number().nullable().transform(emptyStringToNull).notRequired()

          return yup
            .number()
            .nullable()
            .transform(emptyStringToNull)
            .required(
              t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                Required_Field: t('FileMappingSetup:addFileMapping.label.skipRows'),
              }),
            )
            .min(
              yup.ref('skipRowsFrom'),
              t('FileMappingSetup:addFileMapping.validation.skipRowsToGreaterThanFrom'),
            )
            .max(
              SKIP_ROWS_MAX,
              t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                Max_Column: SKIP_ROWS_MAX,
                From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
              }),
            )
        }),
      dateInFileDontIncludeSeparators: yup.boolean(),
      issuedDateFormat: yup.string().when(['sampleFile', 'fileContainsIssuedDate', 'status'], (val) => {
        console.log("status:" +val[2]);
        if (val[0] && val[1] && !val[2]) {
          return yup.string().required(
            t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
              Required_Field: t('FileMappingSetup:addFileMapping.label.issuedDateFormat'),
            }),
          )
        }
        return yup.string().notRequired()
      }),
    })
    .when((values: IFileMapping[], schema: any) => {
      let schemaUpdated = {}
      if (values[0]?.formatType !== 'txt') {
        schemaUpdated = {
          ...schemaUpdated,
          checkNumber: yup.string().when('sampleFile', (val) => {
            if (val[0]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.checkNumber'),
                }),
              )
            }
            return yup.string().notRequired()
          }),
          amount: yup.string().when('sampleFile', (val) => {
            if (val[0]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.amount'),
                }),
              )
            }
            return yup.string().notRequired()
          }),
          issuedDate: yup.string().when(['sampleFile', 'fileContainsIssuedDate'], (val) => {
            if (val[0] && val[1]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.issuedDate'),
                }),
              )
            }
            return yup.string().notRequired()
          }),
        }
      } else {
        schemaUpdated = {
          ...schemaUpdated,
          checkNumberFrom: yup.string().when('sampleFile', (val) => {
            if (val[0]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.checkNumber'),
                }),
              )
            }
            return yup.string().notRequired()
          }),
          checkNumberTo: yup.string().when('sampleFile', (val) => {
            if (val[0]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.checkNumber'),
                }),
              )
            }
            return yup.string().notRequired()
          }),

          amountFrom: yup.string().when('sampleFile', (val) => {
            if (val[0]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.amount'),
                }),
              )
            }
            return yup.string().notRequired()
          }),
          amountTo: yup.string().when('sampleFile', (val) => {
            if (val[0]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.amount'),
                }),
              )
            }
            return yup.string().notRequired()
          }),

          issuedDateFrom: yup.string().when(['sampleFile', 'fileContainsIssuedDate'], (val) => {
            if (val[0] && val[1]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.issuedDate'),
                }),
              )
            }
            return yup.string().notRequired()
          }),
          issuedDateTo: yup.string().when(['sampleFile', 'fileContainsIssuedDate'], (val) => {
            if (val[0] && val[1]) {
              return yup.string().required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.issuedDate'),
                }),
              )
            }
            return yup.string().notRequired()
          }),
        }
      }
      if (values[0]?.formatType === 'txt') {
        if (values[0]?.checkNumberFrom) {
          schemaUpdated = {
            ...schemaUpdated,
            checkNumberFrom: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.checkNumberTo ? Number(value) <= Number(values[0]?.checkNumberTo) : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.checkNumberTo && values[0]?.amountTo && values[0]?.amountFrom
                    ? Number(value) > Number(values[0]?.amountTo) ||
                        Number(values[0]?.checkNumberTo) < Number(values[0]?.amountFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.checkNumberTo && values[0]?.issuedDateTo && values[0]?.issuedDateFrom
                    ? Number(value) > Number(values[0]?.issuedDateTo) ||
                        Number(values[0]?.checkNumberTo) < Number(values[0]?.issuedDateFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.checkNumberTo && values[0]?.accountNumberTo && values[0]?.accountNumberFrom
                    ? Number(value) > Number(values[0]?.accountNumberTo) ||
                        Number(values[0]?.checkNumberTo) < Number(values[0]?.accountNumberFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.checkNumberTo && values[0]?.issuedPayeeTo && values[0]?.issuedPayeeFrom
                    ? Number(value) > Number(values[0]?.issuedPayeeTo) ||
                        Number(values[0]?.checkNumberTo) < Number(values[0]?.issuedPayeeFrom)
                    : true
                },
              ),
          }
        }
        if (values[0]?.checkNumberTo) {
          schemaUpdated = {
            ...schemaUpdated,
            checkNumberTo: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              ),
          }
        }
        if (values[0]?.amountFrom) {
          schemaUpdated = {
            ...schemaUpdated,
            amountFrom: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.amountTo ? Number(value) <= Number(values[0]?.amountTo) : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.amountTo && values[0]?.checkNumberTo && values[0]?.checkNumberFrom
                    ? Number(value) > Number(values[0]?.checkNumberTo) ||
                        Number(values[0]?.amountTo) < Number(values[0]?.checkNumberFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.amountTo && values[0]?.issuedDateTo && values[0]?.issuedDateFrom
                    ? Number(value) > Number(values[0]?.issuedDateTo) ||
                        Number(values[0]?.amountTo) < Number(values[0]?.issuedDateFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.amountTo && values[0]?.accountNumberTo && values[0]?.accountNumberFrom
                    ? Number(value) > Number(values[0]?.accountNumberTo) ||
                        Number(values[0]?.amountTo) < Number(values[0]?.accountNumberFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.amountTo && values[0]?.issuedPayeeTo && values[0]?.issuedPayeeFrom
                    ? Number(value) > Number(values[0]?.issuedPayeeTo) ||
                        Number(values[0]?.amountTo) < Number(values[0]?.issuedPayeeFrom)
                    : true
                },
              ),
          }
        }
        if (values[0]?.amountTo) {
          schemaUpdated = {
            ...schemaUpdated,
            amountTo: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              ),
          }
        }
        if (values[0]?.issuedDateFrom) {
          schemaUpdated = {
            ...schemaUpdated,
            issuedDateFrom: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedDateTo ? Number(value) <= Number(values[0]?.issuedDateTo) : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedDateTo && values[0]?.checkNumberTo && values[0]?.checkNumberFrom
                    ? Number(value) > Number(values[0]?.checkNumberTo) ||
                        Number(values[0]?.issuedDateTo) < Number(values[0]?.checkNumberFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedDateTo && values[0]?.amountTo && values[0]?.amountFrom
                    ? Number(value) > Number(values[0]?.amountTo) ||
                        Number(values[0]?.issuedDateTo) < Number(values[0]?.amountFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedDateTo && values[0]?.accountNumberTo && values[0]?.accountNumberFrom
                    ? Number(value) > Number(values[0]?.accountNumberTo) ||
                        Number(values[0]?.issuedDateTo) < Number(values[0]?.accountNumberFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedDateTo && values[0]?.issuedPayeeTo && values[0]?.issuedPayeeFrom
                    ? Number(value) > Number(values[0]?.issuedPayeeTo) ||
                        Number(values[0]?.issuedDateTo) < Number(values[0]?.issuedPayeeFrom)
                    : true
                },
              ),
          }
        }
        if (values[0]?.issuedDateTo) {
          schemaUpdated = {
            ...schemaUpdated,
            issuedDateTo: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              ),
          }
        }
        if (values[0]?.accountNumberFrom || values[0]?.accountNumberTo) {
          schemaUpdated = {
            ...schemaUpdated,
            accountNumberFrom: yup
              .number()
              .nullable()
              .required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.accountNumber'),
                }),
              )
              .transform((value) => (Number.isNaN(value) ? null : value))
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.accountNumberTo ? Number(value) <= Number(values[0]?.accountNumberTo) : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.accountNumberTo && values[0]?.checkNumberTo && values[0]?.checkNumberFrom
                    ? Number(value) > Number(values[0]?.checkNumberTo) ||
                        Number(values[0]?.accountNumberTo) < Number(values[0]?.checkNumberFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.accountNumberTo && values[0]?.amountTo && values[0]?.amountFrom
                    ? Number(value) > Number(values[0]?.amountTo) ||
                        Number(values[0]?.accountNumberTo) < Number(values[0]?.amountFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.accountNumberTo && values[0]?.issuedDateTo && values[0]?.issuedDateFrom
                    ? Number(value) > Number(values[0]?.issuedDateTo) ||
                        Number(values[0]?.accountNumberTo) < Number(values[0]?.issuedDateFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.accountNumberTo && values[0]?.issuedPayeeTo && values[0]?.issuedPayeeFrom
                    ? Number(value) > Number(values[0]?.issuedPayeeTo) ||
                        Number(values[0]?.accountNumberTo) < Number(values[0]?.issuedPayeeFrom)
                    : true
                },
              ),
            accountNumberTo: yup
              .number()
              .nullable()
              .transform((value) => (Number.isNaN(value) ? null : value))
              .required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.accountNumber'),
                }),
              )
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              ),
          }
        }
        if (values[0]?.accountNumberTo) {
          schemaUpdated = {
            ...schemaUpdated,
            accountNumberTo: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              ),
          }
        }

        if (values[0]?.issuedPayeeFrom || values[0]?.issuedPayeeTo) {
          schemaUpdated = {
            ...schemaUpdated,
            issuedPayeeFrom: yup
              .number()
              .nullable()
              .transform((value) => (Number.isNaN(value) ? null : value))
              .required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.issuedPayee'),
                }),
              )
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedPayeeTo ? Number(value) <= Number(values[0]?.issuedPayeeTo) : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedPayeeTo && values[0]?.checkNumberTo && values[0]?.checkNumberFrom
                    ? Number(value) > Number(values[0]?.checkNumberTo) ||
                        Number(values[0]?.issuedPayeeTo) < Number(values[0]?.checkNumberFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedPayeeTo && values[0]?.amountTo && values[0]?.amountFrom
                    ? Number(value) > Number(values[0]?.amountTo) ||
                        Number(values[0]?.issuedPayeeTo) < Number(values[0]?.amountFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedPayeeTo && values[0]?.issuedDateTo && values[0]?.issuedDateFrom
                    ? Number(value) > Number(values[0]?.issuedDateTo) ||
                        Number(values[0]?.issuedPayeeTo) < Number(values[0]?.issuedDateFrom)
                    : true
                },
              )
              .test(
                'validRange',
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_FROM_MUST_LESS_THAN_NUMBER_TO, {
                  From: min,
                  To: max,
                }),
                (value) => {
                  return values[0]?.issuedPayeeTo && values[0]?.accountNumberTo && values[0]?.accountNumberFrom
                    ? Number(value) > Number(values[0]?.accountNumberTo) ||
                        Number(values[0]?.issuedPayeeTo) < Number(values[0]?.accountNumberFrom)
                    : true
                },
              ),
            issuedPayeeTo: yup
              .number()
              .nullable()
              .transform((value) => (Number.isNaN(value) ? null : value))
              .required(
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                  Required_Field: t('FileMappingSetup:addFileMapping.label.issuedPayee'),
                }),
              )
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.from'),
                }),
              ),
          }
        }
        if (values[0]?.issuedPayeeTo) {
          schemaUpdated = {
            ...schemaUpdated,
            issuedPayeeTo: yup
              .number()
              .min(
                min,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_MUST_FROM_ONE, {
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              )
              .max(
                max,
                t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NUMBER_TO_MUST_LESS_THAN_MAX_COLUMN, {
                  Max_Column: max,
                  From_or_To: t('FileMappingSetup:addFileMapping.placeholder.to'),
                }),
              ),
          }
        }
      }

      return schema.shape(schemaUpdated)
    })

  return validationSchema
}
const EXTRA_COLUMN = 100
const LIMIT_FILE_SIZE = 100000000 // 100000000 bytes = 100MB

/** Rows shown in mapping preview after trimming preamble. */
export const SHEET_ROWS_PREVIEW_DISPLAY = 20

/** Minimum non-empty cells for a row to count as structured tabular (per product assumptions). */
export const STRUCTURED_TABLE_MIN_NONEMPTY_COLUMNS = 3

const MIN_STRUCTURED_NONEMPTY_COLUMNS = STRUCTURED_TABLE_MIN_NONEMPTY_COLUMNS

/** Max allowed mismatch of non-empty counts between adjacent structured rows */
const STRUCT_ROW_COUNT_MISMATCH_TOLERANCE = 5

/** Max allowed mismatch between right-most filled column indices between those rows */
const STRUCT_ROW_WIDTH_MISMATCH_TOLERANCE = 5

function normalizeSheetCellForScan(cell: unknown): string {
  if (cell == null || cell === '') return ''
  if (cell instanceof Date) return cell.toISOString()
  return String(cell).trim()
}

function countNonEmptyRowCells(row: unknown): number {
  if (!Array.isArray(row)) return 0
  return row.map(normalizeSheetCellForScan).filter(Boolean).length
}

/** Last column index that has non-empty trimmed content (-1 if row is entirely empty). */
function rightmostNonEmptyColumnIndex(row: unknown): number {
  if (!Array.isArray(row)) return -1
  let last = -1
  row.forEach((cell, idx) => {
    if (normalizeSheetCellForScan(cell)) last = idx
  })
  return last
}

function rowsLookLikeAdjacentStructuredBands(rowA: unknown, rowB: unknown): boolean {
  const filledA = countNonEmptyRowCells(rowA)
  const filledB = countNonEmptyRowCells(rowB)
  if (filledA < MIN_STRUCTURED_NONEMPTY_COLUMNS || filledB < MIN_STRUCTURED_NONEMPTY_COLUMNS) return false

  if (Math.abs(filledA - filledB) > STRUCT_ROW_COUNT_MISMATCH_TOLERANCE) return false

  const rightA = rightmostNonEmptyColumnIndex(rowA)
  const rightB = rightmostNonEmptyColumnIndex(rowB)
  if (rightA < 0 || rightB < 0) return false

  if (Math.abs(rightA - rightB) > STRUCT_ROW_WIDTH_MISMATCH_TOLERANCE) return false

  return true
}

function findNextNonemptyRowIndex(rows: unknown[][], afterIndex: number, maxExclusive: number): number {
  for (let i = Math.max(afterIndex + 1, 0); i < maxExclusive; i++) {
    if (countNonEmptyRowCells(rows[i]) > 0) return i
  }
  return -1
}

function stripCurrencySymbols(s: string): string {
  return s.replace(/[$\u20ac\u00a3\u00a5]/gi, '').trim()
}

/**
 * Acceptance: signed / accounting-formatted numeric cells use absolute value in preview.
 * Header row labels are untouched when {@link fileContainsHeaderRow} is true (handled in core).
 */
export function normalizeNumericCellForMapping(cell: unknown): unknown {
  if (cell instanceof Date) return cell
  if (typeof cell === 'number' && Number.isFinite(cell)) {
    return Math.abs(cell)
  }
  if (typeof cell === 'string') {
    const trimmed = cell.trim()
    if (!trimmed) return cell

    const parenMatch = trimmed.match(/^\(\s*([\d,.$\s]+)\s*\)$/)
    const core = parenMatch ? parenMatch[1] : trimmed
    const withoutCurrencyComma = stripCurrencySymbols(core).replace(/,/g, '')
    const compact = withoutCurrencyComma.replace(/\s+/g, '')

    // Explicit + or - (e.g. "+1000", "-1000") normalized via absolute value per acceptance criteria.
    if (/^[+-]?\d+(?:\.\d+)?$/.test(compact)) {
      const n = parseFloat(compact)
      if (Number.isFinite(n)) return String(Math.abs(n))
    }
  }
  return cell
}


/**
 * US32690 - Row Skip Range: drops the rows the user declared as ignorable (1-based physical row
 * numbers, inclusive) BEFORE the existing preamble/blank/column detection runs. Returns the input
 * untouched when the range is not configured, so mappings without a range preview exactly as before.
 */
export function applySkipRowsRange<T>(rows: T[], from?: number | null, to?: number | null): T[] {
  if (!rows?.length || from == null || to == null) return rows

  return rows.filter((_row, index) => {
    const rowNumber = index + 1
    return rowNumber < from || rowNumber > to
  })
}

/**
 * Same as {@link applySkipRowsRange} for readers that receive the raw file text (CSV and TXT).
 * Physical lines are counted here - including blank ones - because the CSV parser discards blank
 * lines and would otherwise shift the row numbers the user sees in the file.
 */
export function applySkipRowsRangeToText(text: string, from?: number | null, to?: number | null): string {
  if (typeof text !== 'string' || from == null || to == null) return text

  return applySkipRowsRange(text.split(/\r\n|\n|\r/gm), from, to).join('\n')
}

export function findFirstHeaderRowIndex(rows: unknown[][], fileContainsHeaderRow: boolean): number {
  if (!rows?.length) return 0

  const maxScan = Math.min(rows.length, 120)

  let fallbackStructured = -1

  for (let i = 0; i < maxScan; i++) {
    const row = rows[i]
    if (!Array.isArray(row)) continue

    const nonEmptyCount = countNonEmptyRowCells(row)
    if (nonEmptyCount < MIN_STRUCTURED_NONEMPTY_COLUMNS) continue

    if (fallbackStructured === -1) fallbackStructured = i

    if (fileContainsHeaderRow) return i

    const j = findNextNonemptyRowIndex(rows, i, maxScan)
    if (j >= 0 && rowsLookLikeAdjacentStructuredBands(row, rows[j])) return i
  }

  return fallbackStructured === -1 ? 0 : fallbackStructured
}

export function trimLeadingPreambleRows(
  rows: unknown[][],
  fileContainsHeaderRow: boolean,
  skipPreambleDetection = false,
): unknown[][] {
  if (!rows?.length) return []
  if (skipPreambleDetection) return rows.slice()
  const start = findFirstHeaderRowIndex(rows, fileContainsHeaderRow)
  return rows.slice(start)
}

export function dropRowsOnlyInNonHeaderColumns(
  rows: unknown[][],
  fileContainsHeaderRow: boolean,
): unknown[][] {
  if (!fileContainsHeaderRow || !rows?.length) return rows

  const headerRow = rows[0]
  if (!Array.isArray(headerRow)) return rows

  const headerColumns = new Set<number>()
  headerRow.forEach((cell, idx) => {
    if (normalizeSheetCellForScan(cell)) headerColumns.add(idx)
  })

  if (headerColumns.size === 0) return rows

  return rows.filter((row, idx) => {
    if (idx === 0) return true
    if (!Array.isArray(row) || row.length === 0) return true

    let hasAnyValue = false
    let hasHeaderColumnValue = false
    for (let i = 0; i < row.length; i++) {
      if (!normalizeSheetCellForScan(row[i])) continue
      hasAnyValue = true
      if (headerColumns.has(i)) {
        hasHeaderColumnValue = true
        break
      }
    }

    return !hasAnyValue || hasHeaderColumnValue
  })
}

/**
 * Computes which column indices should be kept after the empty-column filter.
 * Returns the array of original 0-based column indices that survive (length M ≤ maxLen).
 * - fileContainsHeaderRow=true: keep columns whose header cell (row 0) has content.
 * - fileContainsHeaderRow=false: keep columns where at least one structured row has content
 *   (structured = ≥ max(MIN_STRUCTURED_NONEMPTY_COLUMNS, ⌈maxLen/2⌉) non-empty cells).
 */
export function getKeptColumnIndices(
  rows: unknown[][],
  fileContainsHeaderRow: boolean,
): number[] {
  if (!rows?.length) return []

  const maxLen = rows.reduce(
    (acc, row) => (Array.isArray(row) && row.length > acc ? row.length : acc),
    0,
  )
  if (maxLen === 0) return []

  const keepCols: number[] = []

  if (fileContainsHeaderRow) {
    const headerRow = rows[0]
    if (!Array.isArray(headerRow)) {
      for (let i = 0; i < maxLen; i++) keepCols.push(i)
      return keepCols
    }
    for (let i = 0; i < maxLen; i++) {
      if (normalizeSheetCellForScan(headerRow[i])) keepCols.push(i)
    }
  } else {
    // Sparse rows (section markers like "Week of May 10, 26" / weekly totals) shouldn't make a column count as "non-empty".
    // Adaptive threshold: a row must fill at least half of maxLen (and meet the global minimum)
    // to be considered structured. This handles CSVs parsed without a text qualifier, where a
    // marker line like `"Week of May 10, 26",,,,,,,"100,958.96"` splits into many cells with 4
    // non-empty values, which would otherwise pass the static MIN_STRUCTURED_NONEMPTY_COLUMNS threshold.
    const structuredThreshold = Math.max(MIN_STRUCTURED_NONEMPTY_COLUMNS, Math.ceil(maxLen / 2))
    const structuredRows = rows.filter(
      (row) => Array.isArray(row) && countNonEmptyRowCells(row) >= structuredThreshold,
    )
    const scanRows = structuredRows.length > 0 ? structuredRows : rows
    for (let col = 0; col < maxLen; col++) {
      for (const row of scanRows) {
        if (!Array.isArray(row)) continue
        if (normalizeSheetCellForScan(row[col])) {
          keepCols.push(col)
          break
        }
      }
    }
  }

  return keepCols
}

/**
 * Drops columns considered empty so they never reach preview or mapping options.
 * Pairs with {@link getKeptColumnIndices} - exposes that filter as a row-rewrite.
 */
export function dropEmptyColumns(
  rows: unknown[][],
  fileContainsHeaderRow: boolean,
): unknown[][] {
  if (!rows?.length) return rows
  const maxLen = rows.reduce(
    (acc, row) => (Array.isArray(row) && row.length > acc ? row.length : acc),
    0,
  )
  if (maxLen === 0) return rows

  const keepCols = getKeptColumnIndices(rows, fileContainsHeaderRow)
  if (keepCols.length === maxLen) return rows

  return rows.map((row) =>
    Array.isArray(row) ? keepCols.map((c) => (row[c] !== undefined ? row[c] : '')) : row,
  )
}

/**
 * Same trim+row-filter+empty-column pipeline used for the preview, but returns the
 * original-column indices that survive (instead of the rewritten rows).
 * Wrappers call this with raw data so they can keep dropdown `value` / columnDef `colId`
 * aligned with positions in the actual file - critical for the saved mapping to point
 * the backend at the correct column when an issued-check file is processed.
 */
export function getKeptColumnIndicesForRawData(
  sheetData: unknown[][],
  fileContainsHeaderRow: boolean,
  skipPreambleDetection = false,
): number[] {
  if (!sheetData?.length) return []
  let rows = trimLeadingPreambleRows(sheetData, fileContainsHeaderRow, skipPreambleDetection)
  rows = dropRowsOnlyInNonHeaderColumns(rows, fileContainsHeaderRow)
  return getKeptColumnIndices(rows, fileContainsHeaderRow)
}

function formatSheetDataCore(sheetData: Array<any>, fileContainsHeaderRow: boolean) {
  const firstItemHaveData = sheetData?.find((item) => item?.length)

  return sheetData?.map((item: any, index: number) => {
    if (index === 0) {
      const arrayWithNonEmptyElements = Array.from(item, (item) => item || '')
      return arrayWithNonEmptyElements?.map((cell: any) =>
        fileContainsHeaderRow ? cell : String(normalizeNumericCellForMapping(cell)),
      )
    }

    if (item?.length === 0) return firstItemHaveData?.map(() => '')

    return item?.map((cell: any) => String(normalizeNumericCellForMapping(cell)))
  })
}

/** Full sheet-like grid: trim preamble (optional), cap preview rows; numeric cells as absolute values. */
export function preprocessSheetLikeDataForPreview(
  sheetData: unknown[][],
  fileContainsHeaderRow: boolean,
  previewMaxRows: number = SHEET_ROWS_PREVIEW_DISPLAY,
  skipPreambleDetection = false,
): any[] {
  if (!sheetData?.length) return []
  let rows = trimLeadingPreambleRows(sheetData, fileContainsHeaderRow, skipPreambleDetection)
  rows = dropRowsOnlyInNonHeaderColumns(rows, fileContainsHeaderRow)
  rows = dropEmptyColumns(rows, fileContainsHeaderRow)
  if (previewMaxRows != null && previewMaxRows > 0) {
    rows = rows.slice(0, previewMaxRows)
  }
  return formatSheetDataCore(rows, fileContainsHeaderRow)
}

const formatSheetData = (
  sheetData: Array<any>,
  fileContainsHeaderRow: boolean,
  previewMaxRows: number = SHEET_ROWS_PREVIEW_DISPLAY,
  skipPreambleDetection = false,
) => {
  return preprocessSheetLikeDataForPreview(sheetData, fileContainsHeaderRow, previewMaxRows, skipPreambleDetection)
}

const spliceNewline = (text: string) => {
  return text.replaceAll('\r\n', '').replaceAll('\n', '').replaceAll('\r', '')
}

export {
  defaultValues,
  generateValidationSchema,
  LIMIT_FILE_SIZE,
  EXTRA_COLUMN,
  formatSheetData,
  mappingFieldsDefault,
  spliceNewline,
}
